import React from 'react';
import { ChefHat, Heart, Users, Award } from 'lucide-react';

const About = () => {
  const values = [
    {
      icon: <Award className="w-12 h-12" />,
      title: "Quality Over Everything",
      description: "We source the freshest ingredients and maintain the highest standards in every dish we serve"
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: "Customer Satisfaction First",
      description: "Your happiness is our priority. We listen, adapt, and strive to exceed expectations"
    },
    {
      icon: <ChefHat className="w-12 h-12" />,
      title: "Culinary Excellence",
      description: "Our expert chefs bring years of experience and passion to create unforgettable flavors"
    },
    {
      icon: <Users className="w-12 h-12" />,
      title: "Warm Hospitality",
      description: "Humble, attentive service that makes every guest feel like family"
    }
  ];

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative h-[400px] flex items-center justify-center bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1600')" }}>
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Crafting Culinary Memories Since Years
          </h1>
          <p className="text-xl text-gray-200">
            The story of passion, quality, and unwavering commitment to excellence
          </p>
        </div>
      </section>

      {/* Our Story */}
      <section className="section-padding">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
                Our Story
              </h2>
              <div className="space-y-4 text-gray-700 text-lg">
                <p>
                  For over 15 years, The Grill has been Jabalpur's premier dining destination, serving authentic North Indian, Chinese, and Continental cuisine with unwavering dedication to quality and taste.
                </p>
                <p>
                  What started as a passion for creating exceptional dining experiences has grown into a beloved restaurant where families create lasting memories over delicious meals.
                </p>
                <p>
                  Our commitment to maintaining the same great taste year after year has earned us the trust and love of thousands of customers who return time and again.
                </p>
                <p className="font-semibold text-[#E84118]">
                  "The taste is the same since so many years. It's a foodie's paradise!" - Our Customers
                </p>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img
                src="https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800"
                alt="Restaurant interior"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Makes Us Special */}
      <section className="section-padding bg-[#FFF8F0]">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              What Makes Us Special
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Every detail matters when it comes to creating the perfect dining experience
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-[#E84118]">Humble, Trained Staff</h3>
              <p className="text-gray-700">
                Our team is known for politeness, helpfulness, and genuine care for every guest. We take pride in making you feel welcome.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-[#E84118]">Fresh Food Daily</h3>
              <p className="text-gray-700">
                We prepare everything fresh each day using the finest ingredients, ensuring every dish meets our high standards.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-[#E84118]">Perfect Balance</h3>
              <p className="text-gray-700">
                Our chefs masterfully balance spices, salt, and oil to create dishes that are flavorful without being overwhelming.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-3 text-[#E84118]">Customer-Centric Approach</h3>
              <p className="text-gray-700">
                We listen to your preferences and adapt to your needs, ensuring every visit is a delightful experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Chef */}
      <section className="section-padding">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-[#E84118] flex items-center justify-center">
              <ChefHat className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
              Our Chef
            </h2>
            <p className="text-xl text-gray-700 mb-4">
              "Their chef is literally awesome!" - Customer Testimonial
            </p>
            <p className="text-gray-600 text-lg">
              Our culinary team brings together years of experience, passion, and expertise in North Indian, Chinese, and Continental cuisines. Led by our talented head chef, we create dishes that honor tradition while delighting modern palates.
            </p>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="section-padding bg-[#2C3E50] text-white">
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Our Values
            </h2>
            <p className="text-gray-300 text-lg max-w-2xl mx-auto">
              The principles that guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center">
                <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-white/10 flex items-center justify-center text-[#F39C12]">
                  {value.icon}
                </div>
                <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                <p className="text-gray-300">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-[#FFF8F0]">
        <div className="container text-center">
          <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif", color: '#2C3E50' }}>
            Experience The Difference
          </h2>
          <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who have made The Grill their favorite dining destination
          </p>
          <a href="tel:+919876543210" className="btn-primary">
            Reserve Your Table
          </a>
        </div>
      </section>
    </div>
  );
};

export default About;
